import React from "react";
import {Stack, SxProps, Link, Typography, Box} from "@mui/material";
import { CrowdRiffSection } from "@/types/dto/destinations-page/destination-page-types";
import { SectionHeader } from "@/components/common/section-header";
import CrowdRiffGallery from "./crowd-riff-gallery";

export interface SocialLink {
  href: string;
  ariaLabel: string;
  icon: React.ReactNode;
  label: string;
}

export interface CrowdRiffSectionProps {
  crowdRiffSection: CrowdRiffSection;
  showCopyText?: boolean;
  sectionHeaderCentered?: boolean;
  moduleRankingPosition?: string;
  containerSx?: SxProps;
  headerSx?: SxProps;
  socialLink?: SocialLink;
}

const CrowdRiffGallerySection = ({
  sectionHeaderCentered = false,
  crowdRiffSection,
  showCopyText = true,
  moduleRankingPosition = "14",
  headerSx = {},
  containerSx = {},
  socialLink
}: CrowdRiffSectionProps) => {
  // Access env synchronously; this is fine for client components if env is exposed
  const crowdRiffSrc = process.env.CROWD_RIFF_SRC || "https://starling.crowdriff.com/js/crowdriff.js";
  
  if(crowdRiffSection.crowdRiffId) {
    return (
      <Stack
        component="section"
        data-track="visibility"
        data-parent-container-type="gallery"
        data-module-name="CrowdRiff Gallery Section"
        data-ranking-algorithm="manual"
        data-module-ranking-position={moduleRankingPosition}
        sx={{
          padding: "3.2rem 0",
          gap: "2.4rem",
          ...containerSx
        }}
      >
        <SectionHeader
          headerText={crowdRiffSection.basicInfo.headerText}
          headerSx={{
            textAlign: showCopyText && !sectionHeaderCentered ? undefined : "center",
            ...headerSx
          }}
          sx={sectionHeaderCentered ? { textAlign: "center" } : {}}
          {...(showCopyText
            ? {
              copyText: crowdRiffSection.basicInfo.copyText,
              copyTestId: "destinations-page-crowdriff-gallery-subheading"
            }
            : {})}
          headerTestId="destinations-page-crowdriff-gallery-section-heading"
        />
        {socialLink && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.4rem",
              mt: "1.6rem"
            }}
          >
            <Link
              href={socialLink.href}
              target="_blank"
              rel="noopener noreferrer"
              underline="none"
              aria-label={socialLink.ariaLabel}
              data-testid="social-media-link"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "0.4rem"
              }}
            >
              {socialLink.icon}
              <Typography
                variant="subtitle_lg"
                component="span"
                data-testid="social-media-link-label"
                sx={{
                  fontSize: "1.8rem",
                  lineHeight: "normal",
                  color: "secondary.main"
                }}
              >
                {socialLink.label}
              </Typography>
            </Link>
          </Box>
        )}
        <CrowdRiffGallery src={crowdRiffSrc ?? ""} scriptId={crowdRiffSection.crowdRiffId} />
      </Stack>
    );
  }
  return null;
};

export default CrowdRiffGallerySection;
